import type { ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { fadeRise, staggerContainer, VIEWPORT } from "./motion";

interface StaggerProps {
  children: ReactNode;
  className?: string;
  /**
   * Trigger on mount instead of on scroll. Use for the above-the-fold hero,
   * which is already in view on load; leave `false` for every section below it.
   */
  onMount?: boolean;
}

/**
 * Container that reveals its `<StaggerItem>` children in sequence (design.md
 * §12). Drives the shared `initial` / animate states; children inherit them
 * through Framer Motion's variant propagation. Reduced-motion renders a plain
 * wrapper so nothing animates.
 */
export function Stagger({ children, className, onMount = false }: StaggerProps) {
  const reduced = useReducedMotion();
  if (reduced) {
    return <div className={className}>{children}</div>;
  }
  const trigger = onMount
    ? { animate: "visible" }
    : { whileInView: "visible", viewport: VIEWPORT };
  return (
    <motion.div
      className={className}
      variants={staggerContainer}
      initial="hidden"
      {...trigger}
    >
      {children}
    </motion.div>
  );
}

interface StaggerItemProps {
  children: ReactNode;
  className?: string;
}

/** A single child within `<Stagger>`; inherits the parent's reveal timing. */
export function StaggerItem({ children, className }: StaggerItemProps) {
  const reduced = useReducedMotion();
  if (reduced) {
    return <div className={className}>{children}</div>;
  }
  return (
    <motion.div className={className} variants={fadeRise}>
      {children}
    </motion.div>
  );
}
